import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiX, FiUpload, FiStar } from 'react-icons/fi';
import API from '../utils/api';
import { toast } from 'react-toastify';
import './AdminPages.css';

const EMPTY_TRIP = {
  title:'', destination:'', description:'', duration:'', price:'', originalPrice:'',
  maxGroupSize:15, difficulty:'Moderate', category:'Offbeat', tripType:'Domestic',
  rating:4.5, reviewCount:0, featured:false, upcoming:true, isActive:true,
  highlights:'', inclusions:'', exclusions:'', coverImage:''
};

const CATEGORIES = ['Adventure','Cultural','Wildlife','Beach','Mountain','Offbeat','Spiritual'];
const DIFFICULTIES = ['Easy','Moderate','Challenging','Extreme'];
const TRIP_TYPES = ['Domestic','International'];

export default function AdminTrips() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // null | 'add' | 'edit'
  const [form, setForm] = useState(EMPTY_TRIP);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchTrips = () => {
    setLoading(true);
    API.get('/api/admin/trips')
      .then(r => setTrips(r.data.data))
      .catch(() => toast.error('Failed to load trips'))
      .finally(() => setLoading(false));
  };

  useEffect(fetchTrips, []);

  const openAdd = () => { setForm(EMPTY_TRIP); setImageFile(null); setImagePreview(''); setModal('add'); };
  const openEdit = (trip) => {
    setForm({
      ...trip,
      tripType: trip.tripType || 'Domestic',
      highlights: Array.isArray(trip.highlights) ? trip.highlights.join('\n') : trip.highlights || '',
      inclusions: Array.isArray(trip.inclusions) ? trip.inclusions.join('\n') : trip.inclusions || '',
      exclusions: Array.isArray(trip.exclusions) ? trip.exclusions.join('\n') : trip.exclusions || '',
      itinerary: trip.itinerary ? JSON.stringify(trip.itinerary) : '',
    });
    setImagePreview(trip.coverImage || '');
    setImageFile(null);
    setModal('edit');
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      const fields = { ...form };

      // Convert textarea strings → JSON arrays
      ['highlights', 'inclusions', 'exclusions'].forEach(f => {
        if (typeof fields[f] === 'string') {
          fields[f] = JSON.stringify(
            fields[f].split('\n').map(l => l.trim()).filter(Boolean)
          );
        } else if (Array.isArray(fields[f])) {
          fields[f] = JSON.stringify(fields[f]);
        }
      });

      // Itinerary: already JSON string from openEdit, or array — ensure it's a JSON string
      if (fields.itinerary) {
        if (typeof fields.itinerary !== 'string') {
          fields.itinerary = JSON.stringify(fields.itinerary);
        }
        // Validate it's parseable — if not, drop it (don't send broken data)
        try { JSON.parse(fields.itinerary); } catch { delete fields.itinerary; }
      }

      // Strip internal Mongoose fields that cause 400 errors
      ['_id', '__v', 'slug', 'createdAt', 'updatedAt'].forEach(f => delete fields[f]);

      Object.entries(fields).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== '') fd.append(k, v);
      });
      if (imageFile) fd.append('coverImage', imageFile);

      if (modal === 'add') {
        await API.post('/api/admin/trips', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Trip created successfully!');
      } else {
        await API.put(`/api/admin/trips/${form._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Trip updated successfully!');
      }
      setModal(null);
      fetchTrips();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save trip');
    }
    setSaving(false);
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This action cannot be undone.`)) return;
    try {
      await API.delete(`/api/admin/trips/${id}`);
      toast.success('Trip deleted');
      fetchTrips();
    } catch { toast.error('Delete failed'); }
  };

  const filtered = trips.filter(t =>
    t.title?.toLowerCase().includes(search.toLowerCase()) ||
    t.destination?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="admin-page-header">
        <div><h2>Trips</h2><p>{trips.length} trips total</p></div>
        <button className="btn-admin-primary" onClick={openAdd}><FiPlus /> Add New Trip</button>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <div className="admin-filters" style={{ margin: 0 }}>
            <div className="admin-search">
              <FiSearch />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search trips..." />
              {search && <button style={{ position:'absolute', right:10, background:'none', border:'none', cursor:'pointer' }} onClick={() => setSearch('')}><FiX /></button>}
            </div>
          </div>
        </div>
        <div className="admin-table-wrap">
          {loading ? <div className="spinner" style={{ margin: '40px auto' }} /> : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Image</th><th>Title</th><th>Destination</th><th>Type</th><th>Duration</th>
                  <th>Price</th><th>Rating</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length > 0 ? filtered.map(trip => (
                  <tr key={trip._id}>
                    <td>
                      <img className="thumb" src={trip.coverImage || '/ladakh.jpg'}
                        alt={trip.title} onError={e => { e.target.src = '/ladakh.jpg'; }} />
                    </td>
                    <td>
                      <strong style={{ display:'block' }}>{trip.title}</strong>
                      <small style={{ color:'var(--text-light)' }}>{trip.category}</small>
                    </td>
                    <td>{trip.destination}</td>
                    <td>
                      <span style={{
                        padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700,
                        background: trip.tripType === 'International' ? '#eff6ff' : '#f0fdf4',
                        color: trip.tripType === 'International' ? '#1d4ed8' : '#15803d',
                        border: `1px solid ${trip.tripType === 'International' ? '#bfdbfe' : '#bbf7d0'}`
                      }}>
                        {trip.tripType === 'International' ? '🌏 Intl' : '🇮🇳 Dom'}
                      </span>
                    </td>
                    <td>{trip.duration}</td>
                    <td>
                      ₹{trip.price?.toLocaleString()}
                      {trip.originalPrice && <small style={{ display:'block', color:'var(--text-light)', textDecoration:'line-through' }}>₹{trip.originalPrice?.toLocaleString()}</small>}
                    </td>
                    <td><FiStar style={{ color:'var(--secondary)', marginRight:3 }} />{trip.rating}</td>
                    <td>
                      <span className={`badge-status ${trip.isActive ? 'active' : 'inactive'}`}>{trip.isActive ? 'Active' : 'Inactive'}</span>
                      {trip.featured && <span className="badge-status gold" style={{ marginLeft:4 }}>Featured</span>}
                    </td>
                    <td>
                      <div className="action-btns">
                        <button className="btn-action edit" onClick={() => openEdit(trip)} title="Edit"><FiEdit2 /></button>
                        <button className="btn-action delete" onClick={() => handleDelete(trip._id, trip.title)} title="Delete"><FiTrash2 /></button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={8}><div className="admin-empty">No trips found</div></td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {modal && (
          <motion.div className="admin-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={e => e.target === e.currentTarget && setModal(null)}>
            <motion.div className="admin-modal wide" initial={{ opacity: 0, scale: 0.92, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0 }}>
              <div className="admin-modal-header">
                <h3>{modal === 'add' ? 'Add New Trip' : `Edit: ${form.title}`}</h3>
                <button className="admin-modal-close" onClick={() => setModal(null)}><FiX /></button>
              </div>
              <div className="admin-modal-body">
                <form id="trip-form" className="admin-form" onSubmit={handleSubmit}>
                  <div className="form-row-2">
                    <div className="form-group">
                      <label>Trip Title <span>*</span></label>
                      <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} required />
                    </div>
                    <div className="form-group">
                      <label>Destination <span>*</span></label>
                      <input value={form.destination} onChange={e => setForm({...form, destination: e.target.value})} required />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Description <span>*</span></label>
                    <textarea rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} required />
                  </div>
                  <div className="form-row-3">
                    <div className="form-group">
                      <label>Duration <span>*</span></label>
                      <input placeholder="e.g. 7N/8D" value={form.duration} onChange={e => setForm({...form, duration: e.target.value})} required />
                    </div>
                    <div className="form-group">
                      <label>Price (₹) <span>*</span></label>
                      <input type="number" value={form.price} onChange={e => setForm({...form, price: e.target.value})} required />
                    </div>
                    <div className="form-group">
                      <label>Original Price (₹)</label>
                      <input type="number" value={form.originalPrice} onChange={e => setForm({...form, originalPrice: e.target.value})} />
                    </div>
                  </div>
                  <div className="form-row-3">
                    <div className="form-group">
                      <label>Category</label>
                      <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                        {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Difficulty</label>
                      <select value={form.difficulty} onChange={e => setForm({...form, difficulty: e.target.value})}>
                        {DIFFICULTIES.map(d => <option key={d}>{d}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Max Group Size</label>
                      <input type="number" value={form.maxGroupSize} onChange={e => setForm({...form, maxGroupSize: e.target.value})} />
                    </div>
                  </div>
                  <div className="form-row-2">
                    <div className="form-group">
                      <label>Trip Type <span>*</span></label>
                      <select value={form.tripType || 'Domestic'} onChange={e => setForm({...form, tripType: e.target.value})}>
                        {TRIP_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Rating</label>
                      <input type="number" step="0.1" min="1" max="5" value={form.rating} onChange={e => setForm({...form, rating: e.target.value})} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Highlights (one per line)</label>
                    <textarea rows={4} placeholder="Pangong Tso Lake visit&#10;Khardung La pass&#10;Nubra Valley camel safari" value={form.highlights} onChange={e => setForm({...form, highlights: e.target.value})} />
                  </div>
                  <div className="form-row-2">
                    <div className="form-group">
                      <label>Inclusions (one per line)</label>
                      <textarea rows={4} placeholder="Accommodation&#10;All meals&#10;Guide" value={form.inclusions} onChange={e => setForm({...form, inclusions: e.target.value})} />
                    </div>
                    <div className="form-group">
                      <label>Exclusions (one per line)</label>
                      <textarea rows={4} placeholder="Airfare&#10;Personal expenses&#10;Insurance" value={form.exclusions} onChange={e => setForm({...form, exclusions: e.target.value})} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Cover Image</label>
                    <div className="img-upload-box">
                      <input type="file" accept="image/*" onChange={handleImageChange} />
                      {imagePreview ? (
                        <img className="current-img" src={imagePreview} alt="Preview" onError={e => { e.target.src='/ladakh.jpg'; }} />
                      ) : (
                        <><div className="upload-icon"><FiUpload /></div><p>Click to upload image (max 10MB)</p></>
                      )}
                    </div>
                    <input style={{ marginTop:8 }} placeholder="Or enter image URL (e.g. /ladakh.jpg)" value={form.coverImage} onChange={e => { setForm({...form, coverImage: e.target.value}); if(!imageFile) setImagePreview(e.target.value); }} />
                  </div>
                  <div style={{ display:'flex', gap:20 }}>
                    <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:'0.88rem' }}>
                      <input type="checkbox" checked={form.featured} onChange={e => setForm({...form, featured: e.target.checked})} />
                      Featured Trip
                    </label>
                    <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:'0.88rem' }}>
                      <input type="checkbox" checked={form.upcoming} onChange={e => setForm({...form, upcoming: e.target.checked})} />
                      Upcoming Trip
                    </label>
                    <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:'0.88rem' }}>
                      <input type="checkbox" checked={form.isActive} onChange={e => setForm({...form, isActive: e.target.checked})} />
                      Active
                    </label>
                  </div>
                </form>
              </div>
              <div className="admin-modal-footer">
                <button className="btn-admin-danger" onClick={() => setModal(null)}>Cancel</button>
                <button className="btn-admin-primary" form="trip-form" type="submit" disabled={saving}>
                  {saving ? 'Saving...' : modal === 'add' ? 'Create Trip' : 'Save Changes'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
